import { useEffect, useRef } from 'react';
import { ZbarBarcodeScanner } from '@lyskraft/zbar-sdk';
import { Drawer, DrawerProps } from 'antd';
import styled from '@emotion/styled';

const StyledDrawer = styled(Drawer)`
  .ant-drawer-content-wrapper {
    height: 65vh;
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    overflow: hidden;
  }

  .ant-drawer-content {
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
  }

  .ant-drawer-header {
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    text-align: center;
    padding: 16px 24px;
  }

  .ant-drawer-body {
    padding: 0;
    height: calc(100% - 64px);
  }
`;

const ScannerContainer = styled.div`
  height: 100%;
  width: 100%;
  background: #000;
  position: relative;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
`;

interface IBarcodeScannerDrawerProps extends Omit<DrawerProps, 'placement'> {
  onScan: (barcode: string) => void;
  trigger: React.ReactNode;
  closeOnScan?: boolean;
}

export const BarcodeScannerDrawer = ({
  onScan,
  trigger,
  closeOnScan = true,
  ...drawerProps
}: IBarcodeScannerDrawerProps) => {
  const { open, onClose } = drawerProps;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const scanner = ZbarBarcodeScanner.getInstance();

  // Keep the latest callbacks in refs so the camera effect below can depend ONLY on `open`.
  // If the effect listed onScan/onClose as deps, any parent re-render that hands it a new
  // callback identity (e.g. an inline `onClose`, or an `onScan` that changes when a pick
  // list reloads) would re-run the effect -- tearing down and restarting the camera stream
  // mid-scan, which surfaces as a frozen viewfinder. Refs let the handlers stay current
  // without retriggering the start/stop cycle.
  const onScanRef = useRef(onScan);
  const onCloseRef = useRef(onClose);
  const closeOnScanRef = useRef(closeOnScan);
  onScanRef.current = onScan;
  onCloseRef.current = onClose;
  closeOnScanRef.current = closeOnScan;

  useEffect(() => {
    if (!open || !containerRef.current) {
      scanner.unsubscribe('scan');
      scanner.stopScanning();
      return;
    }

    scanner.subscribe('scan', (barcode: string) => {
      onScanRef.current(barcode);
      if (closeOnScanRef.current) {
        onCloseRef.current?.({} as any);
      }
    });

    scanner.run(containerRef.current);

    return () => {
      scanner.unsubscribe('scan');
      scanner.stopScanning();
    };
  }, [open, scanner]);

  return (
    <>
      {trigger}

      <StyledDrawer
        title="Scan Barcode"
        placement="bottom"
        open={open}
        onClose={onClose}
        height={'70vh'}
        {...drawerProps}
        footer={null}
        destroyOnHidden
      >
        <ScannerContainer ref={containerRef} />
      </StyledDrawer>
    </>
  );
};
